// WHITELABEL-1 — boot-time org resolution for a white-label host.
//
// Every other entry shape NAMES its org in the URL (`/w/<org>`, a project
// canvas owner segment, `/studio/<owner>`), and reconcileMwtOrgFromUrl already
// solves all three. A white-label host names its org NOWHERE in the URL:
// `https://app.bellforge.eu/` is a bare path, so the shell boots whatever
// workspace the user last used — someone else's, from the customer's point of
// view.
//
// Rather than teach the reconcile a fourth, URL-less source (it is a delicate
// path with documented workspace-isolation invariants), this resolves the host
// to its org/workspace and then hands off to the EXISTING `/w/<org>/<ws>`
// machinery by navigating there once. One code path stays authoritative for
// "which org is this tab", which is the property that made the previous
// three-shape bug fixable at all.
//
// The scope comes from the server (Organization.customDomains, verified rows
// only) — never a hardcoded host→org map, which would put deployment identity
// in the bundle and rot the moment a customer moves.

import _wsShared from '@symbo-ls/workspace-shared'
import { isWhitelabelPreviewHost } from './customDomainHost.js'
import { setHostScopeOrgId } from './hostScopePin.js'
import { urlNamedOrgSlug } from './urlOrgSlug.js'

// Same thin-wrapper idiom mwtRouter.js uses — read the flag through the shared
// package at CALL time, never captured at module scope (the flag caches on
// `globalThis.__mwtFlagCache` and tests reset it between cases).
const shellIsMwtEnabled = (...args) => _wsShared.functions.isMwtEnabled(...args)
// WHITELABEL-HOST-KEEPS-ACTIVE-WORKSPACE-1 — the tab-side pointer setter.
// MWT off it writes THIS ORIGIN's localStorage, MWT on this tab's
// sessionStorage; a white-label host has its own origin, so the write can
// never move a my.symbols tab (activeWorkspacePointer.js owns the split).
const shellSetActiveWorkspacePointer = (...args) =>
  _wsShared.functions.setActiveWorkspacePointer(...args)

const PLATFORM_SUFFIXES = ['symbols.app', 'symbo.ls', 'localhost']

/** Is this a first-party platform host? Those must NEVER hit the scope lookup
 * — `my.symbols.app` behaviour has to stay bit-identical. RELMAN-DEPLOY-
 * WHITELABEL-PREVIEW-BASE-1: a `<org>.pr.symbols.app` host shares a suffix
 * with the platform but is a customer's temporary preview domain — exempted
 * via the SAME check `functions/customDomainHost.js` uses, so the two
 * classifiers cannot drift apart on which hosts the carve-out covers. */
export const isPlatformHostname = (hostname) => {
  const h = String(hostname || '')
    .toLowerCase()
    .split(':')[0]
  if (!h) return true
  if (isWhitelabelPreviewHost(h)) return false
  return PLATFORM_SUFFIXES.some((s) => h === s || h.endsWith(`.${s}`))
}

/** Does the path name an org? — `/w/<org>/…`, `/studio/<owner>`,
 * `/workspaces/<owner>` or a `/<owner>/<project>` canvas (urlOrgSlug.js, the
 * ONE classifier; `root` lets it tell a canvas from an installed app's own
 * sub-page).
 *
 * WHITELABEL-STRICT-TENANT-SCOPE-1 — two corrections, one meaning change:
 *   - a bare `/studio` names NO org. The old test (`seg[0] === 'studio'`)
 *     said it did, so /studio on a white-label host skipped this whole boot
 *     and rendered the account's stored org — measured on
 *     unreal.pr.symbols.app: Keepz's projects on Unreal's domain;
 *   - "two or more segments" is not "names an org": `/crm/leads` is an
 *     app's sub-page. The same skip fired on every such deep link;
 *   - and naming an org no longer exempts a URL from the host's binding: a
 *     white-label host shows ONE tenant, so a URL naming another org is sent
 *     to the host's home (see resolveCustomDomainScope). The old contract —
 *     "a deep link into /w/<other-org> must not be hijacked" — is withdrawn
 *     (Nika 2026-10-15: "strictly isolate all tenants and workspaces"). */
export const pathNamesOrg = (pathname, root) => !!urlNamedOrgSlug(pathname || '/', root)

/**
 * The raw scope row for a host, or null. Split out of the resolver so the
 * boot can fetch it BEFORE `root.orgs` lands (the fetch needs no orgs — only
 * the slug mapping does) and stamp the workspace pointer at the earliest
 * possible moment (WHITELABEL-HOST-KEEPS-ACTIVE-WORKSPACE-1). Fails closed:
 * null on platform hosts, transport faults, non-ok.
 */
export const fetchCustomDomainScope = async (opts) => {
  const o = opts || {}
  const hostname = o.hostname
  const apiBase = String(o.apiBase || '').replace(/\/+$/, '')
  const fetchImpl = o.fetchImpl
  if (!hostname || isPlatformHostname(hostname)) return null
  if (!apiBase || typeof fetchImpl !== 'function') return null
  try {
    const url = `${apiBase}/core/organizations/custom-domain-scope?host=${encodeURIComponent(hostname)}`
    const res = await fetchImpl(url, { credentials: 'omit' })
    if (!res || !res.ok) return null
    const body = await res.json()
    return (body && body.data) || null
  } catch (_) {
    return null
  }
}

/**
 * Resolve the host's scope and decide where the tab belongs.
 *
 * Returns `{ organizationId, workspaceId, workspaceKey, slug, foreign, path }`:
 *   - `foreign` — the URL names an org that is NOT this host's (a `/w/<other>`
 *     deep link, `/studio/<other>`, another owner's canvas, the account's own
 *     personal namespace). WHITELABEL-STRICT-TENANT-SCOPE-1: such a URL is
 *     sent to the host's home — a customer's domain shows ONE tenant.
 *   - `path` — where to send the tab: the host's home for a bare load or a
 *     foreign URL; null when the URL is the host's own route (a bare
 *     `/studio`, an app page, the host's own `/w/<slug>/…`): the tab stays
 *     where it is and the org is only pinned.
 *
 * Returns null (never throws, never redirects) when: the host is a platform
 * host, the host has no verified row, or the lookup fails. Failing closed
 * here means "decide nothing"; the boot snapshot's own binding
 * (seedWhitelabelHostScope → whitelabelScope.js) is what renders no tenant
 * for a host that cannot prove one.
 */
export const resolveCustomDomainScope = async (opts) => {
  const o = opts || {}
  const hostname = o.hostname
  const pathname = o.pathname || '/'
  const apiBase = String(o.apiBase || '').replace(/\/+$/, '')
  const fetchImpl = o.fetchImpl
  const orgs = Array.isArray(o.orgs) ? o.orgs : []

  if (!hostname || isPlatformHostname(hostname)) return null
  if (!apiBase || typeof fetchImpl !== 'function') return null

  const scope =
    o.scope !== undefined ? o.scope : await fetchCustomDomainScope({ hostname, apiBase, fetchImpl })
  if (!scope || !scope.organizationId || !scope.workspaceId) return null

  // The scope carries IDs; `/w/<org>` speaks SLUGS. Map through the orgs the
  // shell already loaded rather than inventing a second identity source.
  const org = orgs.find((x) => {
    const id = x && (x.id || x._id)
    return id && String(id) === String(scope.organizationId)
  })
  const slug = org && (org.slug || org.username)
  if (!slug) return null

  // WORKSPACE SEGMENT: the `ws_`-shaped KEY only, never a raw ObjectId.
  //
  // WHITELABEL-BOOT-LOOP-1 (measured on app.bellforge.eu, 2026-08-19: the tab
  // froze on every signed-in route). The scope's `workspaceId` is a raw
  // ObjectId, but the canonical workspace segment is the HANDLE
  // (`workspaceUrlHandle` = slug → key → id). Emitting the raw id produced a
  // URL the shell's own canonicalizer immediately rewrote away; this resolver
  // then saw a bare path again and re-emitted it. Two writers, opposite
  // directions, forever — a main-thread navigation loop that locked the
  // renderer. A `ws_`-shaped key is different in kind: it IS a first-class
  // handle (orgSwitchUrl.js law: "accept the id alone ONLY when it is already
  // ws_-shaped"), so the canonicalizer claims it and never rewrites it away.
  //
  // WHY the segment is stated at all
  // (WHITELABEL-HOST-KEEPS-ACTIVE-WORKSPACE-1): a bare `/w/<org>` hands the
  // workspace pick to pointer → server claim, and the server claim is the
  // LAST WORKSPACE THE USER USED ANYWHERE — a multi-workspace user who left
  // my.symbols on another workspace of the same org then landed there under
  // the customer's domain. The host's scope row names its workspace; state
  // it when it can be stated safely, fall back to org-only when the key is
  // not handle-shaped.
  const wsKey = String(scope.workspaceKey || '')
  const wsSegment = /^ws_/.test(wsKey) ? wsKey : null
  const home = wsSegment ? `/w/${slug}/${wsSegment}/` : `/w/${slug}/`
  // Which org does the URL name? The caller that holds the shell root passes
  // it (`root`), so a canvas is told apart from an installed app's sub-page.
  const named = urlNamedOrgSlug(pathname, o.root)
  const foreign = !!named && named !== slug
  const bare = pathname === '/' || pathname === ''
  return {
    organizationId: String(scope.organizationId),
    workspaceId: String(scope.workspaceId),
    workspaceKey: wsSegment,
    slug,
    foreign,
    path: bare || foreign ? home : null
  }
}

/**
 * Back-compatible thin wrapper: the canonical path, or null.
 *
 * Kept as its own export because the URL is the only thing the MWT-ON handoff
 * needs, and every existing caller/test speaks in paths.
 */
export const resolveCustomDomainPath = async (opts) => {
  const resolved = await resolveCustomDomainScope(opts)
  return resolved ? resolved.path : null
}

/**
 * Boot-time orchestrator: wait for `root.orgs` (the resolver maps the scope's
 * org ID to a slug through them), resolve, then hand the tab to the canonical
 * `/w/<org>/<ws>` machinery — replaceState + one router dispatch, the same
 * navigation idiom reconcileMwtOrgFromUrl's tenant rescope uses.
 *
 * NEVER awaited by the boot chain: `root.orgs` is populated by
 * loadPrivateData, so blocking that call on this ladder would deadlock the
 * exact data this waits for. The caller fires it, lets loadPrivateData run,
 * and sequences only the URL reconcile behind it (which kills the race where
 * the reconcile's bare-path canonicalize stamps the ACTIVE org's URL first
 * and makes the path look already-scoped).
 *
 * Resolves 'redirected' | 'pinned' | 'none'. `'pinned'` is the MWT-off path:
 * the URL handoff is inert with that flag off, so the org is stated through
 * `sdk.switchOrg(id, { skipPersist: true })` instead and NO navigation happens
 * (WS-WHITELABEL-ORG-PIN-INERT-WITH-MWT-OFF-1). Never rejects — a lookup that
 * fails leaves the shell exactly as it was (fail closed, same contract as the
 * resolver). Platform hosts return immediately: behaviour there stays
 * bit-identical. Injectable (win/apiBase/fetchImpl/delayMs/maxAttempts) for
 * offline tests; the once-per-load guard lives on `el` so auth-refresh
 * re-fires of the signin callback do not re-run the ladder.
 */
export const bootCustomDomainScope = (root, el, opts = {}) => {
  const win = opts.win || (typeof window !== 'undefined' ? window : null)
  if (!win || !win.location) return Promise.resolve('none')
  const hostname = win.location.hostname
  if (isPlatformHostname(hostname)) return Promise.resolve('none')
  // WHITELABEL-BOOT-LOOP-1 — the guard must be PAGE-scoped, not element-
  // scoped. The first cut hung it on `el`, but the shell re-renders (and a
  // navigation re-creates this element), so every re-render handed back a
  // fresh element with a fresh, unset flag — the resolver re-armed and could
  // navigate again. Combined with the id-vs-handle mismatch above that became
  // an endless rewrite loop. `win` is the one object that survives every
  // re-render within a page load, so "at most once per load" is enforceable
  // only there. The element flag stays as a cheap first check.
  if (el && el._customDomainBootDone) return Promise.resolve('none')
  if (win.__symbolsCustomDomainBootDone) return Promise.resolve('none')
  // WHITELABEL-STRICT-TENANT-SCOPE-1 — NO path exempts a white-label tab from
  // this run any more. It used to return here for every path that "named an
  // org" (a bare /studio and every 2-segment app page included), so those
  // loads never pinned the host's org at all.
  const pathname = (win.location && win.location.pathname) || '/'
  if (el) el._customDomainBootDone = true
  win.__symbolsCustomDomainBootDone = true
  const apiBase = opts.apiBase
  const fetchImpl = opts.fetchImpl || (typeof win.fetch === 'function' ? win.fetch.bind(win) : null)
  const maxAttempts = opts.maxAttempts != null ? opts.maxAttempts : 25
  const delayMs = opts.delayMs != null ? opts.delayMs : 200

  // WHITELABEL-HOST-KEEPS-ACTIVE-WORKSPACE-1 — fetch the scope NOW, before
  // the orgs ladder. The fetch needs no orgs (only the slug mapping does),
  // and the moment it lands we stamp the TAB-SIDE workspace pointer so the
  // boot's own precedence (pointer → server claim) picks the host's
  // workspace instead of whatever my.symbols last used. The pointer write is
  // origin-scoped (this host's storage), so no my.symbols tab can move.
  // Fails closed exactly like the resolver: a null scope stamps nothing.
  // The pre-mount seed (whitelabelBrand.js seedWhitelabelHostScope) already
  // resolved this host's scope from the same endpoint: reuse it rather than a
  // second request, whose transient failure would CLEAR the pin the seed set.
  const seeded = root && root.whitelabelHostScope
  const scopeSource =
    seeded && seeded.organizationId
      ? Promise.resolve(seeded)
      : fetchCustomDomainScope({ hostname, apiBase, fetchImpl })
  const scopePromise = scopeSource.then((scope) => {
    if (scope && scope.workspaceId) {
      try {
        shellSetActiveWorkspacePointer(String(scope.workspaceId))
      } catch (_) {}
    }
    // WHITELABEL-HOST-KEEPS-ACTIVE-WORKSPACE-1, the THIRD defect (the
    // switchOrg race). Publish the host's ORG here — the same moment, and for
    // the same reason, as the workspace pointer above: this is the earliest
    // instant we honestly know it, and every tick auth runs before it is a
    // tick that can state the ambient org instead. From here on auth stands
    // down (resolveActiveOrgId consults the pin), so the wrong org is never
    // written at all rather than written and corrected. Full argument in
    // hostScopePin.js. A null/failed scope CLEARS the pin — fail closed.
    setHostScopeOrgId(scope && scope.organizationId)
    // MIRROR the pin into root state for REACTIVE consumers
    // (WhitelabelRequestAccess). The module-level pin is silent: it can
    // land AFTER a subscriber's last evaluation, and nothing re-fires —
    // measured 2026-08-31 (the non-member gate stayed hidden with every
    // other input true). auth keeps reading the MODULE pin (its stand-down
    // must not depend on render timing); the root key exists only so
    // subscribeTo consumers re-evaluate when the pin lands. Same
    // fail-closed contract: a null scope mirrors null.
    try {
      root.update(
        {
          _hostScopeOrgId: (scope && scope.organizationId && String(scope.organizationId)) || null
        },
        { preventFetch: true }
      )
    } catch (_) {}
    return scope
  })

  // WHITELABEL-BOOT-STUCK-AFTER-SIGNIN-1 — releasing the one-shot when the
  // ladder DECIDED NOTHING.
  //
  // The guard above is armed BEFORE the ladder runs, which is right for the
  // loop it prevents but wrong for the signed-out first load. On
  // app.bellforge.eu the first page load is the SIGN-IN page: `root.orgs` is
  // empty because nobody is signed in yet, the ladder waits its 5 s
  // (25 x 200 ms), gives up, and the PAGE-scoped flag stays set for the rest
  // of that page load. The user then signs in, `orgs` populates on the very
  // same load — and the boot can never re-arm. The hub is left on "Opening
  // your workspace…", which has no failure state, forever. Measured
  // 2026-08-23: the backend scope lookup was healthy the whole time and
  // staging.my.symbols.app/w/bellforge/<ws> worked, because a URL that already
  // names the org makes `pathNamesOrg()` true and skips this path entirely.
  //
  // A timeout waiting for `orgs` is NOT a decision — nothing was resolved and,
  // crucially, NOTHING WAS NAVIGATED. Releasing the flag there cannot reopen
  // WHITELABEL-BOOT-LOOP-1, whose loop came from successful navigations
  // re-firing. Every path that actually decides (resolved a target, navigated,
  // or determined this host has no scope) keeps the one-shot.
  const releaseOneShot = () => {
    if (el) el._customDomainBootDone = false
    win.__symbolsCustomDomainBootDone = false
  }

  return new Promise((resolve) => {
    // WAIT FOR THE ORG WE NEED, NOT FOR ANY ORG
    // (WHITELABEL-HOST-KEEPS-ACTIVE-WORKSPACE-1, the SECOND defect).
    //
    // The ladder used to proceed the moment `root.orgs` was non-empty. But
    // resolveCustomDomainScope does not need SOME orgs, it needs THE org the
    // scope names — it maps organizationId to a slug through that list and
    // returns null when the id is absent. So a PARTIAL orgs array passed the
    // gate, the lookup then failed, and the boot resolved 'none': NO redirect,
    // no retry, and the one-shot NOT released (only the timeout path releases
    // it). The tab then keeps the last-used workspace on a customer's own
    // domain — the symptom Nika reported on gita.pr.symbols.app.
    //
    // THIS IS A DIFFERENT DEFECT FROM THE switchOrg RACE. On work.astio.ai the
    // redirect DOES happen and two un-awaited writers then fight over the org;
    // here the redirect never happens at all, so the ordering fix cannot reach
    // it. Two defects, one symptom.
    //
    // Waiting on the specific id closes the window without adding a timer: the
    // ladder already bounds itself, and a scope still unresolved after
    // maxAttempts releases the one-shot and decides nothing, exactly as before.
    // A null scope (not a white-label host) has no id to wait for and keeps the
    // old any-orgs behaviour.
    const hasScopeOrg = (orgs, scope) => {
      if (!scope || !scope.organizationId) return orgs.length > 0
      return orgs.some((x) => x && String(x.id || x._id) === String(scope.organizationId))
    }
    let scopeSeen // undefined until the fetch settles
    scopePromise
      .then((s) => {
        scopeSeen = s === undefined ? null : s
      })
      .catch(() => {
        scopeSeen = null
      })
    const attempt = (n) => {
      const orgs = root && Array.isArray(root.orgs) ? root.orgs : []
      // Also wait while the scope fetch is still in flight (`scopeSeen`
      // undefined). Without that, the very first tick could pass the gate on a
      // partial orgs array before we even know WHICH org to look for — which
      // is the same hole in a smaller window, and it is what the two new tests
      // caught on the first run of this fix.
      if (scopeSeen === undefined || !orgs.length || !hasScopeOrg(orgs, scopeSeen)) {
        if (n >= maxAttempts) {
          releaseOneShot()
          return resolve('none')
        }
        setTimeout(() => attempt(n + 1), delayMs)
        return
      }
      scopePromise
        .then((scope) =>
          resolveCustomDomainScope({ hostname, pathname, apiBase, fetchImpl, orgs, scope, root })
        )
        .then((resolved) => {
          if (!resolved) return resolve('none')
          // Send the tab to `target` — the host's home: on a bare load (the MWT
          // hand-off below) and, WHITELABEL-STRICT-TENANT-SCOPE-1, whenever the
          // URL names ANOTHER tenant. Returns whether it navigated.
          const navigate = (target) => {
            // Never rewrite to the path we are already on. Belt-and-braces
            // against WHITELABEL-BOOT-LOOP-1: even if some future caller
            // defeats the once-guard, a no-op navigation cannot start a loop.
            const here = (win.location && win.location.pathname) || '/'
            if (!target || here === target) return false
            try {
              win.history.replaceState(win.history.state, '', target)
            } catch (_) {}
            if (el && typeof el.router === 'function') {
              try {
                el.router(target, el.getRoot ? el.getRoot() : undefined)
              } catch (_) {}
            }
            return true
          }

          // WS-WHITELABEL-ORG-PIN-INERT-WITH-MWT-OFF-1 — the URL handoff is
          // INERT when the MWT flag is off, so with that flag off this whole
          // resolver used to be a no-op that still consumed its one-shot.
          //
          // The handoff above states the org by NAVIGATING to `/w/<slug>/` and
          // letting the prefix machinery pin the tab. That machinery is gated
          // on the flag at its first line: `reconcileMwtOrgFromUrl` returns
          // immediately when `isMwtEnabled()` is false, and `mwtOnRenderRouter`
          // actively HEALS a prefixed URL back to the bare route in that case.
          // So with `localStorage.mwt='off'` (or `?mwt=0`) nothing ever consumes
          // the prefix: we resolved the right org, emitted the right URL, and
          // the URL was discarded — measured live on app.bellforge.eu
          // 2026-08-25, where `/w/bellforge/ws_UYAOhQyNjm` was rewritten to `/`
          // inside the first second and held there.
          //
          // A single-org user never noticed, because the ambient org is the only
          // org. A MULTI-org user got whatever org the session last used — the
          // customer seeing ANOTHER TENANT'S workspace under their own domain,
          // which is the exact class WHITELABEL-1 exists to prevent.
          //
          // So when the prefix cannot be consumed, state the org through the
          // channel that IS live in that mode: the same client-only rescope
          // `reconcileMwtOrgFromUrl` uses for its cross-org branch —
          // `sdk.switchOrg(id, { skipPersist: true })`. It loads the org into
          // THIS tab's root via the existing onWorkspaceChange pipeline with no
          // Mongo PATCH and no cross-tab broadcast, so a white-label tab cannot
          // move a shared pointer out from under the user's other tabs.
          //
          // Deliberately NOT the other option on the table (force the MWT flag
          // ON for custom-domain hosts): that would silently re-enable per-tab
          // pointer scoping, URL prefixes and convergence gating for a customer
          // who explicitly opted out, changing far more than "which org is this
          // tab" — and the flag's opt-out is a documented affordance.
          if (!shellIsMwtEnabled()) {
            const sdk = opts.sdk || null
            // A URL naming another tenant leaves for the host's BARE home: with
            // the flag off a `/w/` prefix is healed away, and the pin below is
            // what states the org.
            const left = resolved.foreign ? navigate('/') : false
            if (!sdk || typeof sdk.switchOrg !== 'function') {
              return resolve(left ? 'redirected' : 'none')
            }
            // Already this org — no org hop, but the run still DECIDED:
            // the workspace pointer above is this branch's whole point
            // (WHITELABEL-HOST-KEEPS-ACTIVE-WORKSPACE-1: same org, wrong
            // workspace was exactly the case that leaked the my.symbols
            // active workspace onto the customer's host). 'pinned', not
            // 'none', so the one-shot is kept — a decision was made.
            const activeOrgId = sdk._context && sdk._context.activeOrgId
            if (!(activeOrgId && String(activeOrgId) === resolved.organizationId)) {
              try {
                Promise.resolve(
                  sdk.switchOrg(resolved.organizationId, { skipPersist: true })
                ).catch((err) =>
                  console.warn('[whitelabel] org pin failed:', (err && err.message) || err)
                )
              } catch (err) {
                console.warn('[whitelabel] org pin threw:', (err && err.message) || err)
                return resolve(left ? 'redirected' : 'none')
              }
            }
            return resolve(left ? 'redirected' : 'pinned')
          }

          // MWT on. The host's own route — a bare /studio, an app page, its
          // own /w/<slug>/… — stays where it is: the boot already resolved
          // this tab inside the host's tenant (bootResolve states the host's
          // org and workspace pre-render), so there is nothing to hand off.
          if (!resolved.path) return resolve('pinned')
          return resolve(navigate(resolved.path) ? 'redirected' : 'none')
        })
        .catch(() => resolve('none'))
    }
    attempt(0)
  })
}
